import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { getToken } from "../../../utils/tokenService";
import { FaEye } from "react-icons/fa";

const ClientAppointmentLetters = () => {
  const [letters, setLetters] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const letterMap = useMemo(() => {
    const map = new Map();
    letters.forEach((letter) => {
      const userId = letter.userId?._id || letter.userId;
      if (userId) {
        map.set(userId.toString(), letter);
      }
    });
    return map;
  }, [letters]);

  useEffect(() => {
    const loadLetters = async () => {
      setLoading(true);
      setError("");
      const token = await getToken();
      const appointmentRequest = axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/client/appointment-letters`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const employeesRequest = axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/client/offer-letters`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const [lettersResult, employeesResult] = await Promise.allSettled([
        appointmentRequest,
        employeesRequest,
      ]);

      if (lettersResult.status === "fulfilled") {
        setLetters(lettersResult.value.data?.letters || []);
      } else {
        console.error("Client appointment letters fetch failed:", lettersResult.reason);
        toast.error("Unable to load appointment letters.");
        setError("Unable to load appointment letters. Showing employee list.");
      }

      if (employeesResult.status === "fulfilled") {
        setEmployees(employeesResult.value.data?.employees || []);
      } else {
        console.error("Client employees fetch failed:", employeesResult.reason);
        toast.error("Unable to load employee list.");
        setError((prev) => prev || "Unable to load employee list.");
      }

      setLoading(false);
    };
    loadLetters();
  }, []);

  const handleView = (letter) => {
    if (!letter?._id) {
      toast.info("Invalid appointment letter.");
      return;
    }
    navigate(`/client_dashboard/appointment-letter/${letter._id}`);
  };

  if (loading) return <div className="p-4">Loading...</div>;

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-0">Appointment Letters</h4>
          <small className="text-muted">
            {letters.length} issued · {employees.length} employees
          </small>
        </div>
      </div>
      {error && (
        <div className="alert alert-warning mb-3">{error}</div>
      )}
      {letters.length === 0 && employees.length > 0 && (
        <div className="alert alert-info mb-3">
          No appointment letters have been issued yet. The employee list below will help you know who can receive one.
        </div>
      )}
      {!employees.length ? (
        <p className="text-muted">No employees found for this client.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped align-middle">
            <thead className="table-light">
            <tr>
                <th>Employee</th>
                <th>Employee ID</th>
                <th>Contact</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {employees.map((emp) => {
                const letter = letterMap.get(emp._id?.toString());
                const statusLabel = letter?.status || "not issued";
                return (
                  <tr key={emp._id}>
                    <td>
                      {emp.full_Name || "Employee"}
                      <div className="text-muted small">
                        {emp.email || "-"}
                      </div>
                    </td>
                    <td>
                      {emp.employeeId ? `CMPL${emp.employeeId}` : "-"}
                    </td>
                    <td>{emp.contact || "-"}</td>
                    <td>
                      <span
                        className={`badge text-uppercase ${
                          letter ? "bg-info" : "bg-secondary"
                        }`}
                      >
                        {statusLabel}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => handleView(letter)}
                        disabled={!letter}
                      >
                        <FaEye className="me-1" />
                        {letter ? "View" : "Not issued"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ClientAppointmentLetters;
